import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLocationOptions } from '../../component/Admin/shared/useLocationOptions';
import { speciesApi } from '../../api/speciesLivestockRegistryApi';
import { SubModulePanel } from '../../component/shared/CoreCreateModal';
import RegistryCustomFields from '../../component/Admin/FoundationalTables/FormRegistry/RegistryCustomFields';
import { RegistryFormScope } from '../../component/Admin/FoundationalTables/FormRegistry/registryFieldOverrides';
import { conflictsApi } from '../../api/conflictsApi';
import {
  CommitteeEditorModal,
} from '../../component/Conflicts/FormSelect/tabs/ConflictCommitteesTab';
import {
  ConflictEditorModal,
} from '../../component/Conflicts/FormSelect/tabs/ConflictRecordsTab';
import {
  RouteEditorModal,
} from '../../component/Conflicts/FormSelect/tabs/TranshumanceRoutesTab';

const ROUTES = {
  records: '/conflicts-dispute/records',
  committees: '/conflicts-dispute/committees',
  routes: '/conflicts-dispute/routes',
};

const ALLOWED_TABS = ['records', 'committees', 'routes'];

const TAB_LABELS = {
  records: 'Conflict Incident Report',
  committees: 'Conflict Committee Registration',
  routes: 'Transhumance Route Registration',
};

const SUB_MODULE_OPTIONS = ALLOWED_TABS.map((value) => ({
  value,
  label: TAB_LABELS[value] ?? value,
}));

function useSpeciesOptions() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let active = true;

    speciesApi.listAll({ per_page: 1000, page: 1 })
      .then((result) => {
        if (!active) return;
        setRows(Array.isArray(result?.items) ? result.items : []);
      })
      .catch(() => {
        if (active) setRows([]);
      });

    return () => {
      active = false;
    };
  }, []);

  return rows;
}

const API_BY_TAB = {
  records: conflictsApi.records,
  committees: conflictsApi.committees,
  routes: conflictsApi.routes,
};

export default function ConflictsDisputeAddRecord({ viewOnly = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { uuid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { stateOptions } = useLocationOptions();
  const species = useSpeciesOptions();
  const [loadedRow, setLoadedRow] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState('');

  const visibleTabs = useMemo(
    () => (user?.roleId === 'R05' ? ['records'] : ALLOWED_TABS),
    [user?.roleId]
  );

  const activeTab = useMemo(() => {
    const requested = String(searchParams.get('tab') ?? '').trim();
    return visibleTabs.includes(requested) ? requested : 'records';
  }, [searchParams, visibleTabs]);
  const subModuleOptions = useMemo(
    () => SUB_MODULE_OPTIONS.filter((option) => visibleTabs.includes(option.value)),
    [visibleTabs]
  );

  const mode = uuid ? (viewOnly ? 'view' : 'edit') : 'create';

  // ── Edit/View mode: load the existing record ────────────────────────────
  // Prefer the already-normalized row handed over via navigation state (the
  // exact object the table/list already fetched from the live API) — this
  // is known-complete. Fall back to a fresh live GET only when the page is
  // opened without that state (e.g. a direct link or a refresh).
  useEffect(() => {
    if (!uuid) return;
    const seedRow = location.state?.row;
    if (seedRow && (seedRow.uuid === uuid || seedRow.id === uuid)) {
      setLoadedRow(seedRow);
      setLoadingRecord(false);
      return;
    }
    const api = API_BY_TAB[activeTab] ?? API_BY_TAB.records;
    setLoadingRecord(true);
    setLoadError('');
    api.get(uuid)
      .then((row) => setLoadedRow(row))
      .catch((e) => setLoadError(e?.response?.data?.message ?? e.message ?? 'Failed to load record.'))
      .finally(() => setLoadingRecord(false));
  }, [uuid, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const isR05 = user?.roleId === 'R05';
  const isFieldUser = user?.roleId === 'R04' || user?.roleId === 'R05';
  const canManage = !['R02', 'R03', 'R06'].includes(user?.roleId ?? '');

  const speciesOptions = useMemo(
    () => species.map((item) => ({
      value: item.uuid,
      label: item.common_name_nigeria || item.species_code || item.uuid,
    })),
    [species]
  );

  const speciesLabelById = useMemo(
    () => Object.fromEntries(
      species.map((item) => [item.uuid, item.common_name_nigeria || item.species_code || item.uuid])
    ),
    [species]
  );

  const handleClose = useCallback(() => {
    navigate(ROUTES[activeTab] ?? ROUTES.records, { replace: true });
  }, [activeTab, navigate]);

  const handleSubModuleChange = useCallback((value) => {
    if (!visibleTabs.includes(value)) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', value);
      return next;
    }, { replace: true });
  }, [setSearchParams, visibleTabs]);

  const modulePanel = (
    <SubModulePanel
      moduleName="Conflicts & Disputes"
      moduleDescription="Select a sub module to open the matching conflict, committee, or route record form."
      subModuleOptions={subModuleOptions}
      subModule={activeTab}
      onSubModuleChange={handleSubModuleChange}
      disabled={subModuleOptions.length === 1}
    />
  );

  const sharedProps = {
    mode,
    row: mode === 'create' ? null : loadedRow,
    onClose: handleClose,
    ...(mode === 'view'
      ? {
          onEdit: () => navigate(`/conflicts-dispute/edit/${uuid}?tab=${activeTab}`, { state: { row: loadedRow } }),
          canEdit: canManage,
        }
      : {}),
  };

  return (
    <div className="min-h-screen bg-[#f7f7f7] px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        {loadingRecord && (
          <div className="mb-4 rounded-2xl border border-gray-100 bg-white px-4 py-3 text-sm text-gray-500 shadow-sm">
            Loading record…
          </div>
        )}
        {loadError && (
          <div className="mb-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
            {loadError}
          </div>
        )}
        <RegistryFormScope moduleKey="conflicts-dispute" submoduleKey={activeTab}>
          {(mode === 'create' || loadedRow) && (activeTab === 'committees' ? (
            <>
              <RegistryCustomFields moduleKey="conflicts-dispute" submoduleKey={activeTab} />
              <CommitteeEditorModal {...sharedProps} stateOptions={stateOptions} isFieldUser={isFieldUser} modulePanel={modulePanel} />
            </>
          ) : activeTab === 'routes' ? (
            <>
              <RegistryCustomFields moduleKey="conflicts-dispute" submoduleKey={activeTab} />
              <RouteEditorModal {...sharedProps} speciesOptions={speciesOptions} speciesLabelById={speciesLabelById} stateOptions={stateOptions} isFieldUser={isFieldUser} modulePanel={modulePanel} />
            </>
          ) : (
            <>
              <RegistryCustomFields moduleKey="conflicts-dispute" submoduleKey={activeTab} />
              <ConflictEditorModal {...sharedProps} isR05={isR05} isFieldUser={isFieldUser} modulePanel={modulePanel} />
            </>
          ))}
        </RegistryFormScope>
      </div>
    </div>
  );
}
